import { useState, useCallback, useMemo } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../AlertDialog';

interface UseDialogExitWarningOptions {
  onConfirm: () => void;
  title?: string;
  description?: string;
}

export function useDialogExitWarning({
  onConfirm,
  title = 'Unsaved changes',
  description = 'You have unsaved changes. Are you sure you want to close?',
}: UseDialogExitWarningOptions) {
  const [showWarning, setShowWarning] = useState(false);

  const handleClose = useCallback(() => {
    setShowWarning(true);
  }, []);

  const handleConfirmClose = useCallback(() => {
    setShowWarning(false);
    onConfirm();
  }, [onConfirm]);

  const handleCancelClose = useCallback(() => {
    setShowWarning(false);
  }, []);

  const ExitWarningDialog = useMemo(() => {
    return () => {
      if (!showWarning) return null;

      return (
        <AlertDialog open={showWarning} onOpenChange={setShowWarning}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{title}</AlertDialogTitle>
              <AlertDialogDescription>{description}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={handleCancelClose}>
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction onClick={handleConfirmClose}>
                Confirm
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      );
    };
  }, [showWarning, title, description, handleCancelClose, handleConfirmClose]);

  return {
    handleClose,
    ExitWarningDialog,
  };
}
